import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { hooks, utils } from '@broadleaf/admin-components';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import messages from './ContractPriceCheckTool.messages';

const { axios } = utils;
const { useCurrentTenant } = hooks;

// Metadata-driven views get the /api gateway prefix added for them; direct calls must include it
const CHECK_URL = '/api/pricing/contract-price-check';
const LOCATIONS_URL = `${CHECK_URL}/locations`;
// Token scope, which requires the DEALER_PRICE_DATA permission. The admin's axios only attaches
// a bearer token to requests that declare a scope.
const SCOPE = 'DEALER_PRICE_DATA';
// Datahub returns prices as bare numeric strings with no currency, so prices are shown in the
// checked location's default currency. This is used when that is unknown (a location typed by
// hand, or one with no default currency).
const FALLBACK_CURRENCY = 'USD';
const LOCATION_LIST_ID = 'contract-price-check-locations';

const INPUT_CLASSES =
  'tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 focus:tw-border-blue-500';
const LABEL_CLASSES =
  'tw-block tw-text-xs tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-mb-2';

const ContractPriceCheckTool = () => {
  const { formatMessage } = useIntl();
  const tenant = useCurrentTenant();
  const tenantId = tenant?.id;
  // The tool is tenant-level only, so the context is the tenant with no application
  const requestConfig = useMemo(
    () => ({
      scope: SCOPE,
      headers: { 'X-Context-Request': JSON.stringify({ tenantId }) }
    }),
    [tenantId]
  );
  const [locationId, setLocationId] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [email, setEmail] = useState('');
  const [catalog, setCatalog] = useState('aurora');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [waitingMs, setWaitingMs] = useState(0);
  const [roundTripMs, setRoundTripMs] = useState(null);
  const [resultCurrency, setResultCurrency] = useState(FALLBACK_CURRENCY);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationsStatus, setLocationsStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    axios
      .get(LOCATIONS_URL, requestConfig)
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        if (Array.isArray(data)) {
          setLocations(data);
          setLocationsStatus('loaded');
        } else {
          console.error('Unexpected response from', LOCATIONS_URL, data);
          setLocationsStatus('failed');
        }
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) {
          setLocationsStatus('failed');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestConfig]);

  const locationNames = useMemo(
    () =>
      locations.reduce((names, location) => {
        names[location.locationId.toUpperCase()] = location.active
          ? location.name
          : formatMessage(messages.locationInactive, { name: location.name });
        return names;
      }, {}),
    [locations, formatMessage]
  );

  useEffect(() => {
    if (!loading) {
      return undefined;
    }
    const start = performance.now();
    setWaitingMs(0);
    const interval = setInterval(
      () => setWaitingMs(Math.round(performance.now() - start)),
      100
    );
    return () => clearInterval(interval);
  }, [loading]);

  const locationCurrencies = useMemo(
    () =>
      locations.reduce((currencies, location) => {
        if (location.currency) {
          currencies[location.locationId.toUpperCase()] = location.currency;
        }
        return currencies;
      }, {}),
    [locations]
  );

  const handleSubmit = async e => {
    e.preventDefault();
    const params = {
      locationId: locationId.trim(),
      partNumber: partNumber.trim(),
      email: email.trim(),
      catalog
    };
    const errors = {};
    ['locationId', 'partNumber', 'email'].forEach(field => {
      if (!params[field]) {
        errors[field] = formatMessage(messages.required);
      }
    });
    setFieldErrors(errors);
    if (!isEmpty(errors)) {
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);
    setRoundTripMs(null);
    // Fixed now, so editing the location field later doesn't reformat this result
    const currency = usableCurrency(
      locationCurrencies[params.locationId.toUpperCase()]
    );

    // Browser-side time from sending the request to having the result in hand
    const started = performance.now();
    try {
      // POST so the customer's email isn't logged as part of the URL
      const { data } = await axios.post(CHECK_URL, params, requestConfig);
      setResultCurrency(currency);
      setResponse(data);
    } catch (err) {
      console.error(err);
      setError(toError(err, formatMessage(messages.requestFailed)));
    } finally {
      setRoundTripMs(Math.round(performance.now() - started));
      setLoading(false);
    }
  };

  const handleClear = () => {
    setLocationId('');
    setPartNumber('');
    setEmail('');
    setCatalog('aurora');
    setFieldErrors({});
    setResponse(null);
    setError(null);
    setRoundTripMs(null);
  };

  return (
    <div className="tw-mx-auto tw-max-w-4xl tw-p-6">
      <div className="tw-mb-6">
        <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900">
          {formatMessage(messages.title)}
        </h1>
        <p className="tw-mt-1 tw-text-sm tw-text-gray-600">
          {formatMessage(messages.description)}
        </p>
      </div>

      <div className="tw-mb-6 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-6 tw-shadow-sm">
        <form onSubmit={handleSubmit} className="tw-space-y-4" noValidate>
          <div className="tw-flex tw-max-w-lg tw-flex-col tw-gap-4">
            <div>
              <Field
                id="contract-price-check-location"
                label={formatMessage(messages.locationLabel)}
                error={fieldErrors.locationId}
              >
                <input
                  id="contract-price-check-location"
                  name="locationId"
                  type="text"
                  list={LOCATION_LIST_ID}
                  autoComplete="off"
                  value={locationId}
                  onChange={e => setLocationId(e.target.value)}
                  placeholder={formatMessage(messages.locationPlaceholder)}
                  className={inputClasses(fieldErrors.locationId)}
                  disabled={loading}
                />
              </Field>
              <datalist id={LOCATION_LIST_ID}>
                {locations.map(location => (
                  <option key={location.locationId} value={location.locationId}>
                    {locationNames[location.locationId.toUpperCase()]}
                  </option>
                ))}
              </datalist>
              <LocationHint
                locationId={locationId}
                locationNames={locationNames}
                locationCurrencies={locationCurrencies}
                status={locationsStatus}
              />
            </div>

            <div>
              <Field
                id="contract-price-check-part-number"
                label={formatMessage(messages.partNumberLabel)}
                error={fieldErrors.partNumber}
              >
                <input
                  id="contract-price-check-part-number"
                  name="partNumber"
                  type="text"
                  value={partNumber}
                  onChange={e => setPartNumber(e.target.value)}
                  placeholder={formatMessage(messages.partNumberPlaceholder)}
                  className={inputClasses(fieldErrors.partNumber)}
                  aria-describedby="contract-price-check-part-number-hint"
                  disabled={loading}
                />
              </Field>
              <p
                id="contract-price-check-part-number-hint"
                className="tw-mt-1 tw-text-xs tw-text-gray-600"
              >
                {formatMessage(messages.partNumberHint)}
              </p>
            </div>

            <Field
              id="contract-price-check-email"
              label={formatMessage(messages.emailLabel)}
              error={fieldErrors.email}
            >
              <input
                id="contract-price-check-email"
                name="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={formatMessage(messages.emailPlaceholder)}
                className={inputClasses(fieldErrors.email)}
                disabled={loading}
              />
            </Field>

            <Field
              id="contract-price-check-catalog"
              label={formatMessage(messages.catalogLabel)}
            >
              <select
                id="contract-price-check-catalog"
                name="catalog"
                value={catalog}
                onChange={e => setCatalog(e.target.value)}
                className={classNames(
                  INPUT_CLASSES,
                  'tw-border-gray-300 tw-bg-white tw-py-2.5'
                )}
                disabled={loading}
              >
                <option value="aurora">
                  {formatMessage(messages.catalogAurora)}
                </option>
                <option value="dealer">
                  {formatMessage(messages.catalogDealer)}
                </option>
              </select>
            </Field>
          </div>

          <div className="tw-flex tw-items-center tw-justify-end tw-space-x-3 tw-pt-4">
            {loading && (
              <span
                className="tw-text-xs tw-text-gray-600"
                data-testid="contract-price-check-waiting"
              >
                {formatMessage(messages.waiting, { ms: waitingMs })}
              </span>
            )}
            <button
              type="button"
              onClick={handleClear}
              disabled={loading}
              className="tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 focus:tw-outline-none"
            >
              {formatMessage(messages.clear)}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={classNames(
                'tw-rounded-md tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 focus:tw-outline-none',
                { 'tw-cursor-not-allowed tw-opacity-50': loading }
              )}
            >
              {formatMessage(loading ? messages.submitting : messages.submit)}
            </button>
          </div>
        </form>
      </div>

      {error && <ErrorPanel error={error} roundTripMs={roundTripMs} />}

      {response && (
        <ResponsePanel
          response={response}
          roundTripMs={roundTripMs}
          currency={resultCurrency}
        />
      )}
    </div>
  );
};

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className={LABEL_CLASSES}>
      {label}
    </label>
    {children}
    {error && (
      <p className="tw-mt-1 tw-text-xs tw-text-red-600" role="alert">
        {error}
      </p>
    )}
  </div>
);

const LocationHint = ({
  locationId,
  locationNames,
  locationCurrencies,
  status
}) => {
  const { formatMessage } = useIntl();
  const trimmed = locationId.trim().toUpperCase();
  let hint = null;
  let warning = false;

  if (status === 'loading') {
    hint = formatMessage(messages.locationsLoading);
  } else if (status === 'failed') {
    hint = formatMessage(messages.locationsLoadFailed);
    warning = true;
  } else if (trimmed) {
    const name = locationNames[trimmed];
    if (name) {
      // Show the currency prices will be formatted in, and flag it when it is only assumed
      const currency = locationCurrencies[trimmed];
      hint =
        usableCurrency(currency) === currency
          ? formatMessage(messages.locationWithCurrency, { name, currency })
          : formatMessage(messages.locationWithAssumedCurrency, {
              name,
              currency: FALLBACK_CURRENCY
            });
    } else {
      hint = formatMessage(messages.locationUnknown);
      warning = true;
    }
  }

  if (!hint) {
    return null;
  }

  return (
    <p
      className={classNames('tw-mt-1 tw-text-xs', {
        'tw-text-gray-600': !warning,
        'tw-text-yellow-700': warning
      })}
      data-testid="location-hint"
    >
      {hint}
    </p>
  );
};

const ErrorPanel = ({ error, roundTripMs }) => {
  const { formatMessage } = useIntl();
  const hasUpstream = error.upstreamStatus != null;
  return (
    <div
      className="tw-mb-6 tw-rounded-md tw-border-l-4 tw-border-red-400 tw-bg-red-50 tw-p-4"
      data-testid="contract-price-check-error"
    >
      <div className="tw-flex">
        <div className="tw-flex-shrink-0">
          <svg
            className="tw-h-5 tw-w-5 tw-text-red-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="tw-ml-3 tw-min-w-0 tw-flex-1">
          <p className="tw-text-sm tw-text-red-700">{error.message}</p>
          <RoundTrip ms={roundTripMs} className="tw-mt-1 tw-text-red-700" />
          {hasUpstream && (
            <>
              <p className="tw-mt-2 tw-text-sm tw-text-red-700">
                {formatMessage(messages.upstreamStatus, {
                  status: error.upstreamStatus
                })}
              </p>
              <p className="tw-mt-2 tw-text-xs tw-font-bold tw-text-red-700">
                {formatMessage(messages.upstreamBody)}
              </p>
              <pre className="tw-mt-1 tw-overflow-x-auto tw-rounded tw-border tw-border-red-100 tw-bg-white tw-p-2 tw-font-mono tw-text-xs tw-text-gray-800">
                {error.upstreamBody ||
                  formatMessage(messages.emptyUpstreamBody)}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ResponsePanel = ({ response, roundTripMs, currency }) => {
  const { formatMessage, formatNumber } = useIntl();
  const parts = response.data || [];
  const noValue = formatMessage(messages.noValue);
  const money = value => formatMoney(value, formatNumber, noValue, currency);
  return (
    <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-6 tw-shadow-sm">
      <div className="tw-mb-4 tw-flex tw-items-baseline tw-justify-between">
        <h2 className="tw-text-sm tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-900">
          {formatMessage(messages.summaryTitle)}
        </h2>
        <RoundTrip ms={roundTripMs} className="tw-text-gray-600" />
      </div>

      {response.error && (
        <p
          className="tw-mb-4 tw-rounded-md tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-700"
          data-testid="contract-price-check-datahub-error"
        >
          {formatMessage(messages.datahubError, { error: response.error })}
        </p>
      )}

      {isEmpty(parts) ? (
        <p
          className="tw-mb-4 tw-rounded-md tw-bg-yellow-50 tw-p-3 tw-text-sm tw-text-yellow-800"
          data-testid="contract-price-check-no-price"
        >
          {formatMessage(messages.noPrice)}
        </p>
      ) : (
        <table className="tw-mb-4 tw-w-full tw-text-left tw-text-sm">
          <thead>
            <tr className="tw-text-xs tw-uppercase tw-text-gray-600">
              <th className="tw-py-2 tw-pr-4">
                {formatMessage(messages.partNumberColumn)}
              </th>
              <th className="tw-py-2 tw-pr-4">
                {formatMessage(messages.contractPriceColumn)}
              </th>
              <th className="tw-py-2 tw-pr-4">
                {formatMessage(messages.locationOverridePriceColumn)}
              </th>
              <th className="tw-py-2">
                {formatMessage(messages.suggestedRetailPriceColumn)}
              </th>
            </tr>
          </thead>
          <tbody>
            {parts.map((part, index) => (
              <tr
                key={`${part.partNumber}-${index}`}
                className="tw-border-t tw-border-gray-100"
              >
                <td className="tw-py-2 tw-pr-4">
                  {part.partNumber || noValue}
                </td>
                <td className="tw-py-2 tw-pr-4 tw-font-bold">
                  {money(part.contractPrice)}
                </td>
                <td className="tw-py-2 tw-pr-4">
                  {money(part.locationOverridePrice)}
                </td>
                <td className="tw-py-2">{money(part.suggestedRetailPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className="tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-700">
        {formatMessage(messages.rawResponseTitle)}
      </h3>
      <pre
        className="tw-overflow-x-auto tw-rounded-md tw-border tw-border-gray-100 tw-bg-gray-50 tw-p-4 tw-font-mono tw-text-xs tw-text-gray-800"
        data-testid="contract-price-check-raw-response"
      >
        {JSON.stringify(response, null, 2)}
      </pre>
    </div>
  );
};

const RoundTrip = ({ ms, className }) => {
  const { formatMessage } = useIntl();
  if (ms == null) {
    return null;
  }
  return (
    <p
      className={classNames('tw-text-xs', className)}
      data-testid="contract-price-check-round-trip"
    >
      {formatMessage(messages.roundTrip, { ms })}
    </p>
  );
};

/**
 * Formats a price string such as "10.5" as money. Anything that isn't a number is shown as
 * returned, so an odd Datahub value is never hidden.
 */
function formatMoney(value, formatNumber, noValue, currency) {
  if (value == null || String(value).trim() === '') {
    return noValue;
  }
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return value;
  }
  return formatNumber(amount, { style: 'currency', currency });
}

/**
 * Returns the given ISO 4217 code if it is usable, else the fallback. react-intl swallows the
 * RangeError an unknown code causes and returns the bare number, so it is checked up front.
 */
function usableCurrency(code) {
  if (!code) {
    return FALLBACK_CURRENCY;
  }
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: code });
    return code;
  } catch (e) {
    return FALLBACK_CURRENCY;
  }
}

function inputClasses(error) {
  return classNames(INPUT_CLASSES, {
    'tw-border-gray-300': !error,
    'tw-border-red-500': error
  });
}

/**
 * Normalizes an axios error into the backend's structured error shape:
 * `{ message, upstreamStatus, upstreamBody }`.
 */
function toError(err, fallbackMessage) {
  const data = err.response?.data;
  if (data && typeof data === 'object') {
    return {
      message: data.message || err.message || fallbackMessage,
      upstreamStatus: data.upstreamStatus,
      upstreamBody: data.upstreamBody
    };
  }
  return { message: err.message || fallbackMessage };
}

export default ContractPriceCheckTool;
export { ContractPriceCheckTool };
